import React, { useCallback, useEffect, useRef, useState } from 'react'
import { EditFormProps } from './types'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import FormGroup from 'react-bootstrap/FormGroup'
import FormControl from 'react-bootstrap/FormControl'
import FormLabel from 'react-bootstrap/FormLabel'
import FormText from 'react-bootstrap/FormText'
import Col from 'react-bootstrap/Col'
import Row from 'react-bootstrap/Row'
import Figure from 'react-bootstrap/Figure'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { run as runHolder } from 'holderjs'
import Select from 'app/components/Select'
import { RootState } from 'app/state/types'
import { getAppIconUrl } from 'app/services/client'
import { onImageLoadError } from 'app/utils/fileUtils'
import { APP_ROUTES } from 'app/components/AppRouter/routes'

export const AppEditableForm = (props: EditFormProps) => {
    const {
        app,
        onSubmit,
        onAppChange,
        onAppSelectChange,
        onAppFileChange,
        isEdit = false,
    } = props
    const { appCategories } = useSelector((state: RootState) => state.appSlice)
    const { cdnInfo } = useSelector((state: RootState) => state.generalSlice)
    const [changedImage, setChangedImage] = useState<boolean>(false)
    const navigate = useNavigate()

    const imageNode = useRef<HTMLImageElement>(null)

    useEffect(() => {
        runHolder({ images: imageNode.current })
    }, [])

    const onBackClickHandler = useCallback(() => {
        navigate(APP_ROUTES.APP_DETAILS.replace(':id', app.id.toString()))
    }, [app])

    const getAppImageUrl = useCallback(() => {
        if (cdnInfo.imageCdn.enable && cdnInfo.imageCdn.url != undefined) {
            const cdnLink = cdnInfo.imageCdn.url
            const cleanCdnLink =
                cdnLink.charAt(cdnLink.length - 1) === '/'
                    ? cdnLink.slice(0, -1)
                    : cdnLink
            return getAppIconUrl(app.id, cleanCdnLink)
        } else {
            return getAppIconUrl(app.id)
        }
    }, [cdnInfo, app.id, getAppIconUrl])

    const AppImagePreview = useCallback(() => {
        if (isEdit && !changedImage) {
            return (
                <img
                    src={getAppImageUrl()}
                    width={256}
                    height={256}
                    onError={onImageLoadError}
                />
            )
        } else if (changedImage) {
            return (
                <img
                    src={app.appIconBlob && app.appIconBlob.data}
                    width={256}
                    height={256}
                />
            )
        } else {
            return app.appIconBlob ? (
                <img
                    src={app.appIconBlob && app.appIconBlob.data}
                    width={256}
                    height={256}
                />
            ) : (
                <Figure.Image
                    ref={imageNode}
                    width={256}
                    height={256}
                    data-src={'holder.js/256x256'}
                />
            )
        }
    }, [getAppImageUrl, app, isEdit, imageNode])

    return (
        <Form className="m-2" onSubmit={onSubmit}>
            {isEdit && (
                <Button
                    className="mb-2"
                    variant="outline-success"
                    onClick={onBackClickHandler}>
                    Back to view
                </Button>
            )}
            <FormGroup className="mb-3">
                <FormLabel>App name</FormLabel>
                <FormControl
                    id="appName"
                    name="appName"
                    value={app.appName || ''}
                    onChange={onAppChange}
                    placeholder="Example App"
                />
            </FormGroup>
            <FormGroup className="mb-3">
                <FormLabel>Package name</FormLabel>
                <FormControl
                    id="packageName"
                    name="packageName"
                    value={app.packageName || ''}
                    onChange={onAppChange}
                    placeholder="com.example.app"
                />
            </FormGroup>
            <FormGroup className="mb-3">
                <FormLabel>Category</FormLabel>
                <Select
                    id="category"
                    name="appCategoryLookupId"
                    value={app.appCategoryLookupId || ''}
                    data={appCategories
                        .map(cat => ({
                            ...cat,
                            name: cat.appCategoryName,
                            value: cat.id,
                        }))
                        .sort((cat1, cat2) =>
                            cat1.appCategoryName.localeCompare(
                                cat2.appCategoryName,
                            ),
                        )}
                    onChange={onAppSelectChange}
                />
            </FormGroup>
            <FormGroup className="mb-3">
                <Row>
                    <Col xs="auto">{AppImagePreview()}</Col>
                    <Col>
                        <FormLabel>App icon file</FormLabel>
                        <FormControl
                            name="appIconBlob"
                            type="file"
                            accept="image/png, image/jpeg"
                            onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                                onAppFileChange(e)
                                setChangedImage(true)
                            }}
                        />
                        <FormText>
                            Tip: The maximum size of the image shouldn't be more
                            than 5Mb and dimensions of the image shouldn't be
                            more than 512x512 pixels.
                        </FormText>
                    </Col>
                </Row>
            </FormGroup>
            <FormGroup className="mb-3">
                <Form.Check
                    label="Game"
                    name="isGame"
                    type="checkbox"
                    inline
                    checked={app.isGame}
                    onChange={onAppChange}
                />
                <Form.Check
                    label="Tablet"
                    name="isTablet"
                    type="checkbox"
                    inline
                    checked={app.isTablet}
                    onChange={onAppChange}
                />
                <Form.Check
                    label="Top App"
                    name="isTopApp"
                    type="checkbox"
                    inline
                    checked={app.isTopApp}
                    onChange={onAppChange}
                />
            </FormGroup>
            <FormGroup className="mb-3">
                <Form.Check
                    label="Approved"
                    name="approved"
                    type="checkbox"
                    inline
                    checked={app.approved}
                    onChange={onAppChange}
                />
            </FormGroup>
            <Button type="submit" variant="success">
                {isEdit ? 'Update' : 'Create'}
            </Button>
        </Form>
    )
}
